import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import CampgroundCard from '@/components/campground-directory/CampgroundCard';
import FAQs from '@/components/campground-directory/FAQs';
import MaxWidth from '@/components/common/layout/MaxWidth';
import Description from '@/components/localities/Description';
import Hero from '@/components/localities/Hero';
import SectionTitle from '@/components/localities/SectionTitle';
import { TOP_MENU_RVS } from '@/components/ui/SearchHeader/SearchHeader';
import { ESearchFilters } from '@/constants/searchFilters';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { trackEvent } from '@/services/track-event';
import { IBreadcrumb } from '@/utility/commonDataTypes';

import css from './CampgroundDirectoryTemplate.module.css';

interface ILocality {
  title: string;
  region_code: string;
  name: string;
  url: string;
}
interface ITopLocalities {
  campground_info: string;
  url: string;
  text: string;
  title: string;
}
interface IOtherDirectories {
  city: string;
  url: string;
  title: string;
}
interface IMeta {
  seo_description: string;
  canonical_url: string;
  title?: string;
  breadcrumbs: IBreadcrumb[];
  content: string;
  locality: ILocality;
  top_localities?: ITopLocalities[];
  other_directories?: IOtherDirectories[];
  url_path: string;
}
export interface IProps {
  metadata: IMeta;
  fallback?: boolean;
}

const CampgroundDirectoryTemplate: React.FC<IProps> = props => {
  const { title, breadcrumbs, content, locality, top_localities, other_directories, url_path } =
    props.metadata;

  const { region_code, name: faqLocationName, url: faqLocationUrl } = locality;

  const campgrounds = (top_localities || []).filter(Boolean);
  const crossLinks = (other_directories || []).filter(Boolean);
  const pageTitle = title;
  const pageDescription = `Find the perfect RV rental in ${locality.title}. Simple, easy and fully insured.`;
  const dispatch = useDispatch();

  useEffect(() => {
    if (locality?.title) {
      dispatch(
        setHeaderSearchFilter({
          address: locality?.title,
          [ESearchFilters.SKIP_DEFAULTS]: true,
        }),
      );
    }
  }, [dispatch, locality?.title]);

  useEffect(() => {
    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'Locality directory by type SRP',
    });
  }, []);

  return (
    <div className={css.campgroundDirectory}>
      <Hero
        title={pageTitle}
        description={pageDescription}
        regionCode={region_code}
        urlPath={url_path}
        breadcrumbs={breadcrumbs}
        lockedSearchCategory={TOP_MENU_RVS}
      />

      {content && content !== ''
        ? title && (
            <MaxWidth className="mt-8">
              <SectionTitle>Discover {title}</SectionTitle>
              <Description description={content} />
            </MaxWidth>
          )
        : title && (
            <MaxWidth className="mt-8">
              <SectionTitle>Discover {title}</SectionTitle>
            </MaxWidth>
          )}

      <MaxWidth className="mb-8 md:mt-6">
        {campgrounds.length > 0 &&
          campgrounds.map(campground => (
            <CampgroundCard key={campground.url} campground={campground} />
          ))}
      </MaxWidth>

      <MaxWidth className="md:my-4">
        <FAQs location={faqLocationName} url={faqLocationUrl} />
      </MaxWidth>

      {crossLinks && (
        <MaxWidth className="items-center my-12">
          <SectionTitle>Discover {title}</SectionTitle>

          <div className="grid grid-cols-1 gap-2 sm:gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
            {crossLinks.map(city => {
              return (
                <div className="md:pr-4" key={city.url}>
                  <a href={city.url} className="text-gray-900">
                    <h3 className="font-medium font-lg font">{city.title}</h3>
                  </a>
                </div>
              );
            })}
          </div>
        </MaxWidth>
      )}
    </div>
  );
};

export default CampgroundDirectoryTemplate;
